'use client';

import { useCallback, useEffect, useRef, useState, memo } from 'react';
import type { GameInstance, Telemetry } from '../race/engine';

// SVG canvas dimensions and scale (must match ProceduralTrack.ts)
const SVG_W = 1200;
const SVG_H = 1950;
const SVG_TO_WORLD = 0.06;

const Minimap = memo(function Minimap({
  pathD,
  vehiclePos,
}: {
  pathD: string;
  vehiclePos: { x: number; z: number };
}) {
  // Convert world position back to SVG coordinates
  const svgX = vehiclePos.x / SVG_TO_WORLD + SVG_W / 2;
  const svgY = vehiclePos.z / SVG_TO_WORLD + SVG_H / 2;

  // Use a hidden SVG path element to get the start point and tangent accurately,
  // just like the 2D game's startingLine.ts does.
  // Match the 2D game: finish line at distance 260 along the path
  const [startX, startY, angle] = (() => {
    if (typeof document === 'undefined') return [0, 0, 0];
    const ns = 'http://www.w3.org/2000/svg';
    const tmpSvg = document.createElementNS(ns, 'svg');
    const tmpPath = document.createElementNS(ns, 'path');
    tmpPath.setAttribute('d', pathD);
    tmpSvg.appendChild(tmpPath);
    tmpSvg.style.position = 'absolute';
    tmpSvg.style.width = '0';
    tmpSvg.style.height = '0';
    document.body.appendChild(tmpSvg);

    const p0 = tmpPath.getPointAtLength(260);
    const p1 = tmpPath.getPointAtLength(261);
    document.body.removeChild(tmpSvg);

    const dx = p1.x - p0.x;
    const dy = p1.y - p0.y;
    const deg = Math.atan2(dy, dx) * (180 / Math.PI) + 90;
    return [p0.x, p0.y, deg];
  })();

  return (
    <div className="absolute top-20 right-2 md:bottom-16 md:top-auto md:right-auto md:left-4 z-20">
      <svg
        viewBox={`0 0 ${SVG_W} ${SVG_H}`}
        className="w-20 md:w-32 h-auto rounded-lg overflow-hidden"
        style={{
          background: 'rgba(0,0,0,0.5)',
          backdropFilter: 'blur(4px)',
          border: '1px solid rgba(255,255,255,0.15)',
        }}
      >
        {/* Track outline */}
        <path
          d={pathD}
          fill="none"
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="100"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Track center line */}
        <path
          d={pathD}
          fill="none"
          stroke="rgba(255,255,255,0.15)"
          strokeWidth="4"
          strokeDasharray="12,24"
          strokeLinecap="round"
        />
        {/* Finish line — checkered rectangle matching 2D game (88 wide, 72 tall) */}
        <defs>
          <pattern id="mc" width="20" height="20" patternUnits="userSpaceOnUse" patternTransform={`rotate(${angle} ${startX} ${startY})`}>
            <rect width="10" height="10" fill="#fff"/>
            <rect x="10" y="10" width="10" height="10" fill="#fff"/>
            <rect x="10" width="10" height="10" fill="#333"/>
            <rect y="10" width="10" height="10" fill="#333"/>
          </pattern>
        </defs>
        <rect
          x={startX - 44}
          y={startY - 36}
          width={88}
          height={72}
          fill="url(#mc)"
          transform={`rotate(${angle} ${startX} ${startY})`}
        />
        {/* Vehicle dot */}
        <circle
          cx={svgX}
          cy={svgY}
          r="25"
          fill="#ffcc00"
          stroke="#000"
          strokeWidth="6"
        />
      </svg>
    </div>
  );
});

const RPM_MAX = 12000;
const RPM_REDLINE = 10500;

const Tachometer = memo(function Tachometer({ telemetry }: { telemetry: Telemetry }) {
  const { speedMph, rpm, gear } = telemetry;

  // Arc from 225° (bottom-left) to -45° (bottom-right) = 270° sweep
  const startAngle = 225;
  const endAngle = -45;
  const sweep = startAngle - endAngle; // 270
  const cx = 60;
  const cy = 60;
  const r = 48;

  const rpmFrac = Math.min(rpm / RPM_MAX, 1);
  const needleAngle = startAngle - rpmFrac * sweep;
  const needleRad = (needleAngle * Math.PI) / 180;

  // Redline start position on the arc
  const redlineFrac = RPM_REDLINE / RPM_MAX;
  const redlineStartAngle = startAngle - redlineFrac * sweep;

  // Generate tick marks
  const ticks = [];
  for (let i = 0; i <= 12; i++) {
    const rpmVal = i * 1000;
    const frac = rpmVal / RPM_MAX;
    const angle = ((startAngle - frac * sweep) * Math.PI) / 180;
    const isMajor = i % 2 === 0;
    const innerR = isMajor ? r - 10 : r - 6;
    ticks.push(
      <line
        key={i}
        x1={cx + Math.cos(angle) * innerR}
        y1={cy - Math.sin(angle) * innerR}
        x2={cx + Math.cos(angle) * r}
        y2={cy - Math.sin(angle) * r}
        stroke={rpmVal >= RPM_REDLINE ? '#ef4444' : 'rgba(255,255,255,0.5)'}
        strokeWidth={isMajor ? 2 : 1}
      />,
    );
    if (isMajor) {
      const labelR = r - 16;
      ticks.push(
        <text
          key={`l${i}`}
          x={cx + Math.cos(angle) * labelR}
          y={cy - Math.sin(angle) * labelR}
          fill={rpmVal >= RPM_REDLINE ? '#ef4444' : 'rgba(255,255,255,0.6)'}
          fontSize="7"
          textAnchor="middle"
          dominantBaseline="central"
        >
          {i}
        </text>,
      );
    }
  }

  // Arc paths for background and redline zone
  function arcPath(startDeg: number, endDeg: number, radius: number) {
    const s = (startDeg * Math.PI) / 180;
    const e = (endDeg * Math.PI) / 180;
    const sx = cx + Math.cos(s) * radius;
    const sy = cy - Math.sin(s) * radius;
    const ex = cx + Math.cos(e) * radius;
    const ey = cy - Math.sin(e) * radius;
    const angleDiff = startDeg - endDeg;
    const largeArc = angleDiff > 180 ? 1 : 0;
    return `M ${sx} ${sy} A ${radius} ${radius} 0 ${largeArc} 1 ${ex} ${ey}`;
  }

  return (
    <div className="absolute bottom-10 right-2 md:bottom-4 md:right-4 z-20">
      <div
        className="relative"
        style={{
          width: 120,
          height: 120,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(4px)',
          borderRadius: '50%',
          border: '1px solid rgba(255,255,255,0.15)',
        }}
      >
        <svg viewBox="0 0 120 120" className="w-full h-full">
          {/* Background arc */}
          <path
            d={arcPath(startAngle, endAngle, r)}
            fill="none"
            stroke="rgba(255,255,255,0.1)"
            strokeWidth="4"
            strokeLinecap="round"
          />
          {/* Redline zone arc */}
          <path
            d={arcPath(redlineStartAngle, endAngle, r)}
            fill="none"
            stroke="rgba(239,68,68,0.4)"
            strokeWidth="4"
            strokeLinecap="round"
          />
          {/* Tick marks */}
          {ticks}
          {/* Needle */}
          <line
            x1={cx}
            y1={cy}
            x2={cx + Math.cos(needleRad) * (r - 4)}
            y2={cy - Math.sin(needleRad) * (r - 4)}
            stroke={rpm >= RPM_REDLINE ? '#ef4444' : '#fff'}
            strokeWidth="2"
            strokeLinecap="round"
          />
          {/* Center dot */}
          <circle cx={cx} cy={cy} r="3" fill="rgba(255,255,255,0.8)" />
          {/* Speed readout */}
          <text
            x={cx}
            y={cy + 16}
            fill="#fff"
            fontSize="18"
            fontWeight="bold"
            textAnchor="middle"
            fontFamily="monospace"
          >
            {speedMph}
          </text>
          <text
            x={cx}
            y={cy + 25}
            fill="rgba(255,255,255,0.5)"
            fontSize="6"
            textAnchor="middle"
          >
            MPH
          </text>
          {/* Gear indicator */}
          <text
            x={cx}
            y={cy + 37}
            fill="rgba(255,255,255,0.8)"
            fontSize="12"
            fontWeight="bold"
            textAnchor="middle"
            fontFamily="monospace"
          >
            {gear}
          </text>
        </svg>
      </div>
    </div>
  );
});

interface RaceGameProps {
  trackId: string;
  trackName: string;
  pathD: string | null;
  svgString: string | null;
  ads?: { width: number; height: number; x: number; y: number; rotation: number; src: string }[];
  onLapComplete?: (timeSeconds: number) => void;
}

export default function RaceGame({ trackId, trackName, pathD, svgString, ads, onLapComplete }: RaceGameProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const gameRef = useRef<GameInstance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [time, setTime] = useState(0);
  const [lapState, setLapState] = useState<'waiting' | 'racing' | 'finished'>('waiting');
  const [vehiclePos, setVehiclePos] = useState<{ x: number; z: number }>({ x: 0, z: 0 });
  const [telemetry, setTelemetry] = useState<Telemetry>({ speedMph: 0, rpm: 0, gear: 1 });

  useEffect(() => {
    if (!canvasRef.current || !containerRef.current) return;

    let disposed = false;
    let timerInterval: ReturnType<typeof setInterval>;

    async function start() {
      try {
        const { createGame } = await import('../race/engine');

        if (disposed) return;

        const game = await createGame(
          canvasRef.current!,
          containerRef.current!,
          { pathD, svgString, ads: ads || [] },
          () => setLoading(false),
        );

        if (disposed) {
          game.dispose();
          return;
        }

        gameRef.current = game;

        timerInterval = setInterval(() => {
          if (game.isRunning()) {
            setTime(game.getElapsedTime());
            setLapState(game.getLapState());
            setVehiclePos(game.getVehiclePosition());
            setTelemetry(game.getTelemetry());
          }
        }, 50); // 20fps update for smooth minimap
      } catch (e: any) {
        console.error('Failed to init race:', e);
        setError(e.message || 'Failed to load 3D engine');
        setLoading(false);
      }
    }

    start();

    return () => {
      disposed = true;
      clearInterval(timerInterval);
      if (gameRef.current) {
        gameRef.current.dispose();
        gameRef.current = null;
      }
    };
  }, [pathD, svgString, ads]);

  // Fire callback when lap finishes
  const lapCompleteCalledRef = useRef(false);
  useEffect(() => {
    if (lapState === 'finished' && !lapCompleteCalledRef.current) {
      lapCompleteCalledRef.current = true;
      onLapComplete?.(time);
    }
  }, [lapState, time, onLapComplete]);

  const handleRestart = useCallback(() => {
    gameRef.current?.restart();
    setTime(0);
    setLapState('waiting');
    lapCompleteCalledRef.current = false;
  }, []);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
  };

  return (
    <div ref={containerRef} className="relative w-full h-screen bg-black overflow-hidden select-none">
      <canvas ref={canvasRef} className="w-full h-full block" />

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1D1D1D] z-50">
          <div className="text-white text-4xl font-[var(--font-bebas-neue)] tracking-wider mb-4">
            LOADING
          </div>
          <div className="text-gray-400 text-lg">{trackName}</div>
          <div className="mt-8 w-48 h-1 bg-gray-700 rounded overflow-hidden">
            <div className="h-full bg-yellow-400 animate-pulse rounded" style={{ width: '60%' }} />
          </div>
        </div>
      )}

      {/* Error overlay */}
      {error && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#1D1D1D] z-50">
          <div className="text-red-500 text-2xl mb-4">Failed to load</div>
          <div className="text-gray-400 text-sm max-w-md text-center">{error}</div>
          <a
            href="/"
            className="mt-6 px-6 py-2 bg-white text-black rounded-full text-sm font-bold"
          >
            Back to Speedtracer
          </a>
        </div>
      )}

      {/* HUD */}
      {!loading && !error && (
        <>
          {/* Top bar */}
          <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 z-20">
            <a
              href="/"
              className="px-4 py-2 bg-black/40 backdrop-blur-sm text-white rounded-full text-sm font-bold hover:bg-black/60 transition-colors"
            >
              BACK
            </a>

            <div className="flex flex-col items-center gap-1">
              {lapState === 'waiting' && (
                <div className="px-3 py-1 bg-yellow-500/80 backdrop-blur-sm text-black rounded-full text-xs font-bold animate-pulse">
                  CROSS THE LINE TO START
                </div>
              )}
              {lapState === 'finished' && (
                <div className="px-3 py-1 bg-green-500/80 backdrop-blur-sm text-black rounded-full text-xs font-bold">
                  FINISHED
                </div>
              )}
              <div className={`px-4 py-2 backdrop-blur-sm text-white rounded-full font-mono text-lg tabular-nums ${
                lapState === 'racing' ? 'bg-red-600/60' : lapState === 'finished' ? 'bg-green-600/60' : 'bg-black/40'
              }`}>
                {formatTime(time)}
              </div>
            </div>

            <button
              onClick={handleRestart}
              className="px-4 py-2 bg-black/40 backdrop-blur-sm text-white rounded-full text-sm font-bold hover:bg-black/60 transition-colors"
            >
              RESTART
            </button>
          </div>

          {/* Minimap */}
          {pathD && (
            <Minimap pathD={pathD} vehiclePos={vehiclePos} />
          )}

          {/* Tachometer */}
          <Tachometer telemetry={telemetry} />

          {/* Track name */}
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-black/30 backdrop-blur-sm text-white/50 rounded-full text-xs z-20">
            {trackName}
          </div>

          {/* Finish overlay */}
          {lapState === 'finished' && (
            <div className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none">
              <div className="pointer-events-auto flex flex-col items-center gap-4 bg-black/70 backdrop-blur-md rounded-2xl px-10 py-8 border border-white/10">
                <div className="text-white/60 text-sm font-bold tracking-widest uppercase">Lap Complete</div>
                <div className="text-white text-5xl font-mono tabular-nums font-bold">
                  {formatTime(time)}
                </div>
                <button
                  onClick={handleRestart}
                  className="mt-2 px-8 py-3 bg-white text-black rounded-full text-sm font-bold hover:bg-white/90 transition-colors"
                >
                  RACE AGAIN
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
